'use client'

import { cn } from '../lib/utils'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import {
  type CSSProperties,
  type ReactNode,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { getThumb } from './image-thumb'
import { type ImageLoader, loadImage } from './load-image'
import { Progress } from './progress'
import { Pattern } from './pattern'
import { stripes } from '../patterns'
import { useTranslation } from '../i18n'
import { CircleAlertIcon } from './icon'
import { Shape } from './shape'
import { cookie6 } from './shapes'
import { useImageLoading } from './image-loading'
import { Button } from './button'
export { ImageLoadingProvider } from './image-loading'

export interface ImageProps {
  src?: string
  alt?: string
  /** Preserve the source image's intrinsic aspect ratio when no ThumbHash dimensions are available. */
  intrinsic?: boolean
  /** object-fit: cover. Pass a string to also set object-position (e.g. cover="top"). */
  cover?: boolean | string
  className?: string
  style?: CSSProperties
  onClick?: () => void
  /** Overlay content (e.g. upload progress indicator). */
  children?: ReactNode
  /** Skip the blur-up thumbnail even if the src encodes one. */
  noThumb?: boolean
  /** Show gradient placeholder when no src. */
  gradient?: boolean
  /** Custom preloader; defaults to an HTML-based one that honours storybook fake-network. */
  loader?: ImageLoader
  ref?: React.Ref<HTMLDivElement>
}

export interface FileImageProps extends Omit<ImageProps, 'src'> {
  file?: Blob
}

const t300 = { duration: 0.3 }
const tNone = { duration: 0 }
const unavailablePattern = stripes({ angle: 45, spacing: 16 / Math.SQRT2 })

/**
 * Image with blur-up loading.
 *
 * The thumb (blurry placeholder) is decoded from a ThumbHash embedded in the
 * image filename (`{v}-{w}-{h}-{base62hash}-{uid}.avif`). It renders instantly
 * from a ~32px PNG data URL — no network request — then crossfades to the full
 * image via AnimatePresence once it loads.
 *
 * For the thumb to work, the src must follow the filename convention above.
 * Falls back to a plain fade-in when the src has no embedded hash.
 *
 * Size the component via className (e.g. `w-64 aspect-video`) or style.
 * Both images are absolutely positioned inside a relative wrapper, so the
 * wrapper must have an explicit size.
 */
export function FileImage({ file, ...rest }: FileImageProps) {
  const src = useBlobUrl(file)
  return <Image {...rest} src={src} />
}

function useBlobUrl(blob?: Blob) {
  const [src, setSrc] = useState('')
  useEffect(() => {
    // oxlint-disable-next-line no-useless-undefined -- consistent-return: this effect has a cleanup return below
    if (!blob?.size) return undefined
    const url = URL.createObjectURL(blob)
    // eslint-disable-next-line rc/set-state-in-effect -- one-time sync when blob changes, not cascading
    setSrc(url)
    // oxlint-disable-next-line rc/url -- local variable, not a URL literal; false positive from rc/url rule
    return () => {
      URL.revokeObjectURL(url)
    }
  }, [blob])
  return src
}

export function Image(props: ImageProps) {
  return <ImageSource key={props.src} {...props} />
}

function ImageSource({
  src,
  alt = '',
  intrinsic,
  cover,
  className,
  style,
  onClick,
  children,
  noThumb,
  gradient,
  loader,
  ref,
}: ImageProps) {
  const { t } = useTranslation('ui')
  const { pending, onRetry } = useImageLoading()
  const [failed, setFailed] = useState(false)
  const waiting = !src && !gradient && pending
  const unavailable = (!src && !gradient && !pending) || failed
  const thumb = useMemo((): ReturnType<typeof getThumb> => {
    if (noThumb || !src) return undefined
    return getThumb(src)
  }, [src, noThumb])

  const [loading, setLoading] = useState(Boolean(thumb && src))

  useEffect((): void | (() => void) => {
    if (noThumb || !src) return
    let cancelled = false
    // eslint-disable-next-line rc/set-state-in-effect -- one-time reset when src changes, not cascading
    setLoading(true)
    // oxlint-disable-next-line typescript/no-floating-promises -- intentional fire-and-forget; loading state resets in finally
    void loadImage(src, loader)
      .catch(() => {
        if (!cancelled) setFailed(true)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [src, noThumb, loader])

  const reducedMotion = useReducedMotion()
  const transition = reducedMotion ? tNone : t300

  const showThumb = Boolean(thumb?.dataUrl && loading && !unavailable)
  // Deferred so the full image gets a render head-start before the thumb exits.
  const showThumbDeferred = useDeferredValue(showThumb)

  const imgStyle: CSSProperties = {}
  if (cover) {
    imgStyle.objectFit = 'cover'
    if (typeof cover === 'string') imgStyle.objectPosition = cover
  }

  // Thumb shares the same object-fit/position so it looks identical to the full image.
  const thumbStyle: CSSProperties = { ...imgStyle }

  const wrapperStyle: CSSProperties = {
    aspectRatio: thumb?.ar,
    ...style,
  }
  const useIntrinsicLayout = Boolean(intrinsic && !thumb)

  return (
    // oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- onClick is optional passthrough; callers that need a11y should use a button wrapper
    <div
      ref={ref}
      className={cn('relative overflow-hidden', (unavailable || waiting) && 'min-h-24', className)}
      style={wrapperStyle}
      onClick={onClick}
    >
      {/* Thumb: shown while full image loads, exits via AnimatePresence */}
      <AnimatePresence>
        {showThumbDeferred && !unavailable && (
          <motion.img
            key="thumb"
            src={thumb?.dataUrl}
            alt=""
            className="absolute inset-0 h-full w-full"
            style={thumbStyle}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={transition}
          />
        )}
      </AnimatePresence>

      {/* Full image: hidden until loaded, then fades in */}
      {!showThumb && src && !unavailable && (
        <motion.img
          key="full"
          src={src}
          alt={alt}
          loading="lazy"
          onError={() => { setFailed(true) }}
          className={
            useIntrinsicLayout
              ? 'block h-auto w-full'
              : 'absolute inset-0 h-full w-full'
          }
          style={imgStyle}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={transition}
        />
      )}

      {/* Gradient fallback when no src */}
      {!src && gradient && <div className="bg-gradient absolute inset-0" />}

      {waiting && <div role="status" aria-label={t('image.loading')} className="bg-surface-2 absolute inset-0 flex items-center justify-center"><Progress variant="circular" size="sm" value={null} /></div>}

      {unavailable && (
        <div role="status" className="bg-surface-2 border-error absolute inset-0 flex items-center justify-center rounded-[inherit] border-4 p-4">
          <Pattern pattern={unavailablePattern} width={320} height={180} fit="cover" className="text-error absolute inset-0 h-full w-full" />
          <div className="text-error relative flex flex-col items-center gap-2 sm:gap-3">
            <div className="relative flex size-20 items-center justify-center sm:size-32">
              <Shape shape={cookie6} className="text-surface-3 absolute inset-0 size-full" />
              <CircleAlertIcon className="relative size-9 sm:size-14" aria-hidden />
            </div>
            <span className="bg-surface-3 rounded-full px-4 py-2 text-center text-sm font-medium select-none sm:px-5 sm:py-3 sm:text-lg">{t('image.unavailable')}</span>
            {onRetry && <Button variant="outlined" size="sm" onClick={() => { setFailed(false); onRetry() }}>{t('image.retry')}</Button>}
          </div>
        </div>
      )}

      {children}
    </div>
  )
}

/** Small circular progress overlay for image upload. Pass as Image children. */
export function ImageUploading({ progress }: { progress: number | null }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center backdrop-blur-md">
      <Progress
        className="text-white"
        value={progress}
        variant="circular"
        size="sm"
      />
    </div>
  )
}
